import { Controller } from "@hotwired/stimulus"

// Generic mobile panel switcher: shows one panel at a time on mobile,
// all panels on desktop. Panels are found via configurable CSS selectors.
export default class extends Controller {
  static values = {
    panel: { type: String, default: "" },
    selectors: { type: Object, default: {} },
    breakpoint: { type: Number, default: 1024 }
  }
  static classes = ["active", "visible"]

  initialize() { this.stack = []; this.scrollPositions = {} }

  connect() {
    this._onResize = () => this._apply()
    window.addEventListener("resize", this._onResize)
    this._clearInlineStyles()
    const panels = Object.keys(this.selectorsValue)
    if (panels.length && this.panelValue && this.panelValue !== panels[0]) {
      this.stack.push(panels[0])
    }
    this._apply()
    this._observe()
  }

  disconnect() {
    window.removeEventListener("resize", this._onResize)
    this._observer?.disconnect()
    this._showAll()
  }

  navigate({ params: { panel } }) {
    if (panel) this.panelValue = panel
  }

  navigateIfMobile({ params: { panel } }) {
    if (this._isMobile() && panel && panel !== this.panelValue) {
      this.panelValue = panel
    }
  }

  back() {
    const target = this.stack.pop() ?? Object.keys(this.selectorsValue)[0]
    if (!target || target === this.panelValue) return
    this._goingBack = true
    this.panelValue = target
  }

  backIfMobile() {
    if (this._isMobile()) this.back()
  }

  close() { this.back() }

  toggle({ params: { selector } }) {
    const el = this.element.querySelector(selector)
    if (!el) return
    el.classList.toggle("tw-hidden")
    if (!el.classList.contains("tw-hidden")) el.querySelector("input")?.focus()
  }

  show({ params: { selector } }) {
    const el = this.element.querySelector(selector)
    if (el) el.classList.remove("tw-hidden")
  }

  hide({ params: { selector } }) {
    const el = this.element.querySelector(selector)
    if (el) el.classList.add("tw-hidden")
  }

  panelValueChanged(value, previousValue) {
    if (!this._isMobile()) return
    if (previousValue) this.scrollPositions[previousValue] = window.scrollY
    const isBack = this._goingBack
    if (isBack) {
      this._goingBack = false
    } else if (previousValue && previousValue !== value) {
      this.stack.push(previousValue)
    }
    this._show()
    window.scrollTo(0, isBack ? (this.scrollPositions[value] || 0) : 0)
  }

  // -- private --

  _isMobile() { return window.innerWidth <= this.breakpointValue }

  _apply() {
    const mobile = this._isMobile()
    if (this.hasActiveClass) this.element.classList.toggle(this.activeClass, mobile)
    if (mobile) { this._show(); this._hideTagged() } else { this._showAll() }
  }

  _show() {
    for (const [name, selector] of Object.entries(this.selectorsValue)) {
      const el = this.element.querySelector(selector)
      if (!el) continue
      const active = name === this.panelValue
      el.classList.toggle("tw-hidden", !active)
      if (this.hasVisibleClass) el.classList.toggle(this.visibleClass, active)
    }
  }

  _showAll() {
    this._clearInlineStyles()
    for (const selector of Object.values(this.selectorsValue)) {
      const el = this.element.querySelector(selector)
      if (!el) continue
      el.classList.remove("tw-hidden")
      if (this.hasVisibleClass) el.classList.remove(this.visibleClass)
    }
    this.element.querySelectorAll("[data-mobile-hide]").forEach(el => el.classList.remove("tw-hidden"))
  }

  _clearInlineStyles() {
    for (const selector of Object.values(this.selectorsValue)) {
      const el = this.element.querySelector(selector)
      if (el) el.style.removeProperty("display")
    }
    this.element.querySelectorAll("[data-mobile-clear]").forEach(el => el.style.removeProperty("display"))
  }

  _hideTagged() {
    this.element.querySelectorAll("[data-mobile-hide]").forEach(el => el.classList.add("tw-hidden"))
  }

  _observe() {
    this._observer = new MutationObserver(() => {
      if (this._isMobile()) { this._clearInlineStyles(); this._show(); this._hideTagged() }
    })
    this._observer.observe(this.element, { childList: true, subtree: true })
  }
}
